import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "form", "nameInput", "descriptionInput", "categorySelect", "submitButton"]
  static values = { createUrl: String }

  connect() {
    // Close modal when clicking outside
    this.boundCloseOnOutsideClick = this.closeOnOutsideClick.bind(this)
    // Close modal when pressing ESC key
    this.boundCloseOnEscape = this.closeOnEscape.bind(this)
  }

  disconnect() {
    if (this.boundCloseOnOutsideClick) {
      document.removeEventListener("click", this.boundCloseOnOutsideClick)
    }
    if (this.boundCloseOnEscape) {
      document.removeEventListener("keydown", this.boundCloseOnEscape)
    }
  }

  showModal() {
    if (!this.hasModalTarget) return
    
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex")
    
    if (this.hasNameInputTarget) {
      this.nameInputTarget.focus()
    }
    
    // Add event listeners for clicking outside modal and ESC key
    setTimeout(() => {
      document.addEventListener("click", this.boundCloseOnOutsideClick)
      document.addEventListener("keydown", this.boundCloseOnEscape)
    }, 100)
  }

  showModalWithName(categoryName) {
    if (!this.hasModalTarget) return
    
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex")
    
    // Pre-fill the name input
    if (this.hasNameInputTarget && categoryName) {
      this.nameInputTarget.value = categoryName.trim()
      // Focus on description field since name is already filled
      if (this.hasDescriptionInputTarget) {
        this.descriptionInputTarget.focus()
      } else {
        this.nameInputTarget.focus()
      }
    } else if (this.hasNameInputTarget) {
      this.nameInputTarget.focus()
    }
    
    // Add event listeners for clicking outside modal and ESC key
    setTimeout(() => {
      document.addEventListener("click", this.boundCloseOnOutsideClick)
      document.addEventListener("keydown", this.boundCloseOnEscape)
    }, 100)
  }

  hideModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      this.modalTarget.classList.remove("flex")
    }
    if (this.hasFormTarget) {
      this.formTarget.reset()
    }
    document.removeEventListener("click", this.boundCloseOnOutsideClick)
    document.removeEventListener("keydown", this.boundCloseOnEscape)
  }

  closeOnOutsideClick(event) {
    if (!this.hasModalTarget) return
    
    const modalContent = this.modalTarget.querySelector("[data-modal-content]")
    if (modalContent && !modalContent.contains(event.target)) {
      this.hideModal()
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape" || event.keyCode === 27) {
      this.hideModal()
    }
  }

  async createCategory(event) {
    event.preventDefault()
    
    if (!this.hasNameInputTarget || !this.hasDescriptionInputTarget) {
      console.error("Missing form input targets")
      return
    }
    
    const name = this.nameInputTarget.value.trim()
    const description = this.descriptionInputTarget.value.trim()
    
    if (!name) {
      this.showError("Category name is required")
      return
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = "Creating..."
    }

    try {
      const formData = new FormData()
      formData.append('category[name]', name)
      formData.append('category[description]', description)
      
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content || 
                        document.querySelector('meta[name="csrf-token"]')?.content
      
      const headers = {
        "Accept": "application/json"
      }
      
      if (csrfToken) {
        headers["X-CSRF-Token"] = csrfToken
      }
      
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: headers,
        body: formData
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()

      if (data.success) {
        // Automatically start timer with the new category
        const timerDropdown = document.getElementById('timerDropdown')
        if (timerDropdown) {
          // Create and submit a form to start the timer with the new category
          const startTimerForm = document.createElement('form')
          startTimerForm.method = 'POST'
          startTimerForm.action = '/time_logs/start_timer'
          startTimerForm.style.display = 'none'
          
          const categoryIdInput = document.createElement('input')
          categoryIdInput.type = 'hidden'
          categoryIdInput.name = 'category_id'
          categoryIdInput.value = data.category.id
          startTimerForm.appendChild(categoryIdInput)
          
          // Add CSRF token
          const csrfToken = document.querySelector('[name="csrf-token"]')?.content || 
                           document.querySelector('meta[name="csrf-token"]')?.content
          if (csrfToken) {
            const csrfInput = document.createElement('input')
            csrfInput.type = 'hidden'
            csrfInput.name = 'authenticity_token'
            csrfInput.value = csrfToken
            startTimerForm.appendChild(csrfInput)
          }
          
          document.body.appendChild(startTimerForm)
          startTimerForm.submit()
          return // Don't show success message since we're redirecting
        }
        
        // Also add to form dropdown if we're on the new/edit form page
        if (this.hasCategorySelectTarget) {
          // Find the searchable dropdown controller
          const searchableDropdownElement = this.categorySelectTarget.closest('[data-controller~="searchable-dropdown"]')
          
          if (searchableDropdownElement) {
            const searchableDropdownController = this.application.getControllerForElementAndIdentifier(searchableDropdownElement, 'searchable-dropdown')
            
            if (searchableDropdownController) {
              // Create new category item element for the form
              const newCategoryItem = document.createElement('li')
              newCategoryItem.setAttribute('data-searchable-dropdown-target', 'categoryItem')
              newCategoryItem.setAttribute('data-category-name', data.category.name.toLowerCase())
              newCategoryItem.setAttribute('data-category-id', data.category.id)
              newCategoryItem.setAttribute('data-category-color', data.category.color || '#3B82F6')
              newCategoryItem.setAttribute('data-action', 'click->searchable-dropdown#selectCategory')
              newCategoryItem.className = 'flex items-center gap-2 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer'
              
              // Add color dot
              const colorDot = document.createElement('div')
              colorDot.className = 'w-3 h-3 rounded-full flex-shrink-0'
              colorDot.style.backgroundColor = data.category.color || '#3B82F6'
              newCategoryItem.appendChild(colorDot)
              
              // Add category content div
              const contentDiv = document.createElement('div')
              contentDiv.className = 'flex-1 min-w-0'
              
              const nameDiv = document.createElement('div')
              nameDiv.className = 'font-medium truncate'
              nameDiv.textContent = data.category.name
              contentDiv.appendChild(nameDiv)
              
              if (data.category.description) {
                const descDiv = document.createElement('div')
                descDiv.className = 'text-xs text-gray-500 dark:text-gray-400 truncate'
                descDiv.textContent = data.category.description
                contentDiv.appendChild(descDiv)
              }
              
              newCategoryItem.appendChild(contentDiv)
              
              // Add to dropdown (before the create option)
              const categoryList = searchableDropdownElement.querySelector('[data-searchable-dropdown-target="categoryList"]')
              const createOption = categoryList.querySelector('[data-searchable-dropdown-target="createOption"]')
              categoryList.insertBefore(newCategoryItem, createOption)
              
              // Auto-select the new category
              if (searchableDropdownController.selectCategory) {
                // Simulate a click event to select the category
                const clickEvent = new Event('click')
                Object.defineProperty(clickEvent, 'currentTarget', { value: newCategoryItem })
                searchableDropdownController.selectCategory(clickEvent)
              }
            }
          } else {
            // Fallback for old select element if it exists
            const option = new Option(data.category.name, data.category.id, true, true)
            this.categorySelectTarget.add(option)
          }
        }
        
        // Show success message
        this.showSuccess(data.message)
        
        // Hide modal and reset form
        this.hideModal()
      } else {
        this.showError(data.errors.join(", "))
      }
    } catch (error) {
      console.error("Error creating category:", error)
      console.error("URL:", this.createUrlValue)
      this.showError(`Failed to create category: ${error.message}`)
    } finally {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.textContent = "Create Category"
      }
    }
  }

  showError(message) {
    // Create or update error message
    this.clearMessages()
    const errorDiv = document.createElement("div")
    errorDiv.className = "p-4 mb-4 text-sm text-red-800 border border-red-300 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400 dark:border-red-800"
    errorDiv.setAttribute("data-error-message", "")
    errorDiv.innerHTML = `
      <div class="flex">
        <svg class="flex-shrink-0 inline w-4 h-4 mr-3 mt-[2px]" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
        </svg>
        <span class="sr-only">Error</span>
        <div>
          <span class="font-medium">Error:</span>
          ${message}
        </div>
      </div>
    `
    if (this.hasFormTarget) {
      this.formTarget.insertBefore(errorDiv, this.formTarget.firstChild)
    }
  }

  showSuccess(message) {
    // Show success message at the top of the form instead of the entire page
    const existingAlert = document.querySelector('[data-success-message]')
    if (existingAlert) {
      existingAlert.remove()
    }

    const successDiv = document.createElement("div")
    successDiv.className = "flex items-center p-4 mb-4 text-sm text-green-800 border border-green-300 rounded-lg bg-green-50 dark:bg-gray-800 dark:text-green-400 dark:border-green-800"
    successDiv.setAttribute("data-success-message", "")
    successDiv.setAttribute("role", "alert")
    successDiv.style.opacity = "0"
    successDiv.style.transition = "opacity 0.3s ease-in-out"
    successDiv.innerHTML = `
      <svg class="flex-shrink-0 inline w-4 h-4 mr-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
        <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
      </svg>
      <span class="sr-only">Info</span>
      <div class="flex-1">${message}</div>
      <button type="button" class="ms-auto -mx-1.5 -my-1.5 bg-green-50 text-green-500 rounded-lg focus:ring-2 focus:ring-green-400 p-1.5 hover:bg-green-200 inline-flex items-center justify-center h-8 w-8 dark:bg-gray-800 dark:text-green-400 dark:hover:bg-gray-700" aria-label="Close">
        <span class="sr-only">Close</span>
        <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
        </svg>
      </button>
    `
    
    // Find the form and insert the message at the top of it
    const timeLogForm = document.querySelector('form[data-controller~="form-shortcuts"]')
    if (timeLogForm) {
      timeLogForm.insertBefore(successDiv, timeLogForm.firstChild)
    } else {
      // Fallback to inserting in the new-category controller element
      this.element.insertBefore(successDiv, this.element.firstChild)
    }
    
    // Add close button functionality
    const closeButton = successDiv.querySelector('button[aria-label="Close"]')
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        successDiv.style.opacity = "0"
        setTimeout(() => {
          if (successDiv.parentNode) {
            successDiv.remove()
          }
        }, 300)
      })
    }
    
    // Smooth fade in
    setTimeout(() => {
      successDiv.style.opacity = "1"
    }, 10)
    
    // Auto-hide after 4 seconds with fade out
    setTimeout(() => {
      successDiv.style.opacity = "0"
      setTimeout(() => {
        if (successDiv.parentNode) {
          successDiv.remove()
        }
      }, 300)
    }, 4000)
  }

  clearMessages() {
    if (this.hasFormTarget) {
      const errorMessage = this.formTarget.querySelector('[data-error-message]')
      if (errorMessage) {
        errorMessage.remove()
      }
    }
  }
}